import React, { useEffect, useState } from "react";
import NavBar from "./components/navBar";
import EquipmentInventoryTable from "./components/inventory";
import Login from "./components/login";
import axios from "axios";
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import About from "./components/about";
import { jsPDF } from 'jspdf'
import { autoTable } from 'jspdf-autotable'

function App() {
  
  const API_BASE_URL = "https://city-van.onrender.com"; 
  
  const [tools, setTools] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState(null);  
  
  const [auth, setAuth] = useState(() => {
    const role = localStorage.getItem("role");
    return {
      isLoggedIn: !!role,
      role: role ? role.toLowerCase() : null  
    };
  });

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const role = localStorage.getItem("role");
        if (role) {
          setAuth({
            isLoggedIn: true,
            role: role
          });
        }
      } catch (error) {
        console.error("Auth check error:", error);
        localStorage.removeItem("role");
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/tools`); 
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const contentType = response.headers.get('content-type');
        if (!contentType?.includes('application/json')) {
          const text = await response.text();
          throw new Error(`Server returned: ${text.substring(0, 100)}`);
        }
        
        const data = await response.json();
        setTools(data);
      } catch(error) {
        console.error("Fetch error:", error);
        setError("Failed to load tools. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const createTool = async (toolData) => {
    try {
      const payload = {
        ...toolData,
        LastMaintenance: toolData.LastMaintenance
          ? new Date(toolData.LastMaintenance).toISOString()  
          : new Date().toISOString()                         
      };
  
      const response = await axios.post(`${API_BASE_URL}/api/addTool`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY || ""
        }
      });
  
      if (response.status === 201) {
        setTools([...tools, response.data]);
        return { success: true, data: response.data };
      }
  
      return { success: false, error: `Unexpected status: ${response.status}` };
    } catch (error) {
      console.error("Error adding tool:", error);
      return { 
        success: false, 
        error: error.response?.data?.error || "Failed to add tool" 
      };
    }
  };
  
  const removeTool = async (toolToRemove) => {
    try {
      const { status } = await axios.delete(`${API_BASE_URL}/api/tools/${toolToRemove._id}`);
      if (status === 200) {
        setTools(tools.filter((tool) => tool._id !== toolToRemove._id));
      }
    } catch(error) {
      console.error("Error deleting tool:", error);
      alert("Failed to delete tool. Please try again.");
    }
  };

  const updateTool = async (toolToUpdate) => {
    try {
      console.log("Updating tool:", toolToUpdate);
  
      const payload = {
        ...toolToUpdate,
        LastMaintenance: toolToUpdate.LastMaintenance
          ? new Date(toolToUpdate.LastMaintenance).toISOString()
          : new Date().toISOString()
      };
  
      const response = await axios.put(
        `${API_BASE_URL}/api/tools/${toolToUpdate._id}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY || "" 
          }
        }
      );
  
      console.log("Backend update response:", response);
  
      if (response.status === 200) {
        setTools(prevTools =>
          prevTools.map(tool =>
            tool._id === toolToUpdate._id ? { ...response.data } : tool
          )
        );
        return { success: true, data: response.data };
      }
  
      return { success: false, error: `Unexpected status: ${response.status}` };
    } catch (error) {
      console.error("Update failed:", error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  };
  
  const signUserIn = async (email, password) => {
    try {
      setAuthLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
  
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Login failed");
      }
  
      const data = await res.json();
      const role = data.role.toLowerCase(); // Ensure lowercase
      localStorage.setItem("role", role);
      
      setAuth({
        isLoggedIn: true,
        role: role
      });
      
      navigate(role === "admin" ? "/admin" : "/", { replace: true });
      
    } catch (err) {
      console.error("Login error:", err);
      alert(err.message || "Login failed. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("role");
    setAuth({
      isLoggedIn: false,
      role: null
    });
    navigate("/", { replace: true });
  };

  const onDownloadPDF = (toolData) => {
    const doc = new jsPDF(); 

    doc.setFontSize(16); 
    doc.text('Equipment Inventory Report', 14, 15); 

    doc.setFontSize(10); 
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 5); 

    const tableColumn = [
      "Unit No", 
      "Category", 
      "Description", 
      "Crew", 
      "Make", 
      "Fuel", 
      "Model", 
      "Serial No", 
      "Status", 
      "Location"
    ];

    const tableRows = []; 
    toolData.forEach(item => {
      const itemData = [
        item.UnitNo,
        item.Category,
        item.Description,
        item.Crew,
        item.Make,
        item.Fuel,
        item.Model,
        item.SerialNo,
        item.Status,
        item.SiteLocation
      ]; 
      tableRows.push(itemData); 
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] }
    }); 

    doc.save('equipment-inventory.pdf')
  }

  const ProtectedRoute = ({ children }) => {
    if (authLoading) return <div>Loading...</div>;
    if (!auth.isLoggedIn || auth.role !== "admin") {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  if (authLoading) {
    return <div>Loading authentication...</div>;
  }

  return (
    <div className="App">
      <NavBar auth={auth} onLogout={handleLogout} key={auth.isLoggedIn ? 'loggedIn' : 'loggedOut'} />       
      <Routes>
        <Route path="/" element={<About />} />
        <Route path="/login" element={<Login signUserIn={signUserIn} />} />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute>
             <EquipmentInventoryTable 
              key={tools.length}
              toolData={tools} 
              onAdd={createTool} 
              onRemove={removeTool} 
              onUpdateForm={updateTool} 
              onDownloadPDF={onDownloadPDF}
            />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </div>
  );
}

export default App;